/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The team the Equipe tab is waiting on.
 *
 * Until the first answer, there is no team — not a team of zero. A later
 * failure keeps the last answer, because a dropped request is not the same
 * thing as everybody having left.
 */

import { useCallback, useEffect, useState } from 'react';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import type { TeamOverview } from '@/services/requests/housien-services/AgenciesService.team';

/** Where the read is. `ready` is the only phase that has a team to draw. */
export type TeamPhase = 'loading' | 'ready' | 'error';

export interface TeamLoad {
  team: TeamOverview | null;
  phase: TeamPhase;
  reload: () => Promise<void>;
}

/**
 * Reads the agency team, and reads it again on demand.
 *
 * @param agencyId - The agency, or undefined before the shell knows it.
 * @returns The last answer, whether it is safe to draw, and a way to read again.
 */
export function useTeamLoad(agencyId: string | undefined): TeamLoad {
  const [team, setTeam] = useState<TeamOverview | null>(null);
  const [phase, setPhase] = useState<TeamPhase>('loading');

  const reload = useCallback(async () => {
    if (!agencyId) return;
    try {
      setTeam(await AgenciesService.getAgencyTeam(agencyId));
      setPhase('ready');
    } catch {
      setPhase((current) => (current === 'ready' ? 'ready' : 'error'));
    }
  }, [agencyId]);

  useEffect(() => { void reload(); }, [reload]);

  return { team, phase, reload };
}
